import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FEATURE_FLAGS } from '../config/featureFlags';

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { LOGIN_ENABLED } = FEATURE_FLAGS;

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/chat', label: 'Information', icon: '💬', protected: false },
    { path: '/learning', label: 'Learning', icon: '📚', protected: true },
    { path: '/degree-plan', label: 'Degree Plan', icon: '🎓', protected: true },
    { path: '/events', label: 'Events', icon: '📅', protected: true },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredNavItems = navItems.filter(
    (item) => !(LOGIN_ENABLED && item.protected && !isAuthenticated)
  );

  return (
    <nav className="bg-[#C41230] text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition">
            <span className="text-2xl font-bold">Campus Link</span>
            <span className="text-sm opacity-90">• Tartan World</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-4">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg transition flex items-center space-x-2 ${
                    isActive ? 'bg-white text-[#C41230]' : 'hover:bg-[#A01028]'
                  }`}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Auth buttons - desktop */}
          <div className="hidden md:flex items-center space-x-2">
            {LOGIN_ENABLED ? (
              isAuthenticated ? (
                <>
                  <div className="flex items-center space-x-2 bg-white bg-opacity-10 px-4 py-2 rounded-lg">
                    <span>👤</span>
                    <span className="text-sm font-medium">{user?.name}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-white text-[#C41230] rounded-lg font-semibold hover:bg-gray-100 transition"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="px-4 py-2 bg-white text-[#C41230] rounded-lg font-semibold hover:bg-gray-100 transition"
                >
                  Login
                </Link>
              )
            ) : (
              <div className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg font-semibold cursor-not-allowed">
                🔒 Login (Coming Soon)
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
              className="text-white focus:outline-none"
            >
              {isMobileMenuOpen ? '✖️' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-2 space-y-2 pb-4">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-2 rounded-lg transition ${
                    isActive ? 'bg-white text-[#C41230]' : 'hover:bg-[#A01028]'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="mr-2">{item.icon}</span>
                  {item.label}
                </Link>
              );
            })}

            {/* Auth buttons - mobile */}
            {LOGIN_ENABLED ? (
              isAuthenticated ? (
                <>
                  <div className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-10 rounded-lg">
                    <span>👤</span>
                    <span className="text-sm font-medium">{user?.name}</span>
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 bg-white text-[#C41230] rounded-lg font-semibold hover:bg-gray-100 transition"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-2 bg-white text-[#C41230] rounded-lg font-semibold hover:bg-gray-100 transition"
                >
                  Login
                </Link>
              )
            ) : (
              <div className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg font-semibold cursor-not-allowed">
                🔒 Login (Coming Soon)
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
